import { Action, UUID } from '../app/basicModels';
import { ofType } from 'redux-observable';
import { map, concatMap, catchError, withLatestFrom } from 'rxjs/operators';
import { of, Observable } from 'rxjs';
import actions from './actions';
import { getPhenomenonCategories, savePhenomenonCategory, getPhenomenonCategory, deletePhenomenonCategory } from './service';
import { PhenomenonCategoryPage, PhenomenonCategory, LoadPhenomenonCategoryQueryPayload } from './models';
import { notification } from '../../components';
import uuid from 'uuid/v4';
import { injectMetadata } from '../request-control';
import { ErrorMsg, notifyErrorOrFallback } from '../../settings/models/Error';
import { AppState } from '../redux.model';
import type { BasicUser } from '../user/models';

type SavePhenomenonCategoryAction = ReturnType<typeof actions.savePhenomenonCategory> & { metadata?: Record<string, unknown> };

interface SavePhenomenonCategoryState {
  currentUser: BasicUser;
  workspaceId?: UUID;
  migratedConfigurationWrites: string;
}

export const handleLoadPhenomenonCategories = action$ =>
  action$.pipe(
    ofType(actions.LOAD_PHENOMENON_CATEGORIES),
    map((action: Action<LoadPhenomenonCategoryQueryPayload>) => action.payload),
    concatMap((query: LoadPhenomenonCategoryQueryPayload) =>
      getPhenomenonCategories(query.pageableQuery, query.companyId, query.showCanonicals).pipe(
        map(response => response.data),
        map((phenomenonCategories: PhenomenonCategoryPage) => {
          return actions.loadPhenomenonCategoriesSuccess(phenomenonCategories);
        }),
        catchError((error: ErrorMsg) => {
          const errorMsg = notifyErrorOrFallback(error, 'Error loading categories!');
          return of(actions.loadPhenomenonCategoriesFailure(errorMsg));
        })
      )
    )
  );

export const handleLoadPhenomenonCategory = action$ =>
  action$.pipe(
    ofType(actions.LOAD_PHENOMENON_CATEGORY),
    map((action: Action<UUID>) => action.payload),
    concatMap((phenomenonCategoryId: UUID) =>
      getPhenomenonCategory(phenomenonCategoryId).pipe(
        map(response => response.data),
        map((phenomenonCategory: PhenomenonCategory) => {
          return actions.loadPhenomenonCategorySuccess(phenomenonCategory);
        }),
        catchError((error: ErrorMsg) => {
          const errorMsg = notifyErrorOrFallback(error, 'Error loading category!');
          return of(actions.loadPhenomenonCategoryFailure(errorMsg));
        })
      )
    )
  );

export const handleSavePhenomenonCategory = (action$, state$) =>
  action$.pipe(
    ofType(actions.SAVE_PHENOMENON_CATEGORY),
    withLatestFrom(
      state$.pipe(
        map((state: AppState) => ({
          currentUser: state.User.currentUser as BasicUser,
          workspaceId: state.MethodologyDeep?.methodologyDeep?.workspace_id as UUID | undefined,
          migratedConfigurationWrites: (state.App?.systemFlags?.P40_55336_MIGRATED_CONFIGURATION_WRITES ?? '[]') as string
        }))
      )
    ),
    concatMap(([action, state]: [SavePhenomenonCategoryAction, SavePhenomenonCategoryState]) => {
      const { currentUser, workspaceId, migratedConfigurationWrites } = state;
      const phenomenonCategory = action.payload;
      const phenomenonCategoryToSave = {
        account: {
          id: currentUser.id,
          name: currentUser.name
        },
        payload: {
          ...phenomenonCategory,
          cohese: true,
          // Workspace clones land with company_id=null + workspace_id set; do not flag them canonical (P40-59492)
          ...(phenomenonCategory.isNew && !phenomenonCategory.company_id && !workspaceId ? { is_canonical: true } : {})
        }
      };
      if (phenomenonCategoryToSave.payload.isNew && !phenomenonCategoryToSave.payload.id) {
        phenomenonCategoryToSave.payload.id = uuid();
      }

      return savePhenomenonCategory(phenomenonCategoryToSave, phenomenonCategoryToSave.payload.isNew, migratedConfigurationWrites).pipe(
        map(response => {
          const savePhenomenonCategorySuccess = injectMetadata(actions.savePhenomenonCategorySuccess, { ...action.metadata, response });
          return savePhenomenonCategorySuccess(phenomenonCategoryToSave.payload);
        }),
        catchError((error: any) => {
          const msg = error.response && error.response.data ? error.response.data.message : '';
          const savePhenomenonCategoryFailure = injectMetadata(actions.savePhenomenonCategoryFailure, {
            ...action.metadata,
            response: error.response
          });
          return of(savePhenomenonCategoryFailure(msg));
        })
      );
    })
  );

export const handleSavePhenomenonCategorySuccess = (action$, state$) =>
  action$.pipe(
    ofType(actions.SAVE_PHENOMENON_CATEGORY_SUCCESS),
    withLatestFrom(
      state$.pipe(
        map((state: any) => {
          return state.PhenomenonCategory.editModalActive;
        })
      )
    ),
    concatMap(([, editModalActive]): Observable<any> => {
      if (editModalActive) {
        return of(actions.togglePhenomenonCategoryEditModal());
      }
      return of();
    })
  );

export const handleDeletePhenomenonCategory = (action$, state$) =>
  action$.pipe(
    ofType(actions.DELETE_PHENOMENON_CATEGORY),
    map((action: Action<PhenomenonCategory>) => action.payload),
    withLatestFrom(
      state$.pipe(
        map((state: any) => {
          return state.User.currentUser;
        })
      )
    ),
    map(([phenomenonCategory, currentUser]: any) => {
      const phenomenonCategoryToSave = {
        account: {
          id: currentUser.id,
          name: currentUser.name
        },
        payload: {
          ...phenomenonCategory
        }
      };
      return phenomenonCategoryToSave;
    }),
    withLatestFrom(
      state$.pipe(map((state: AppState) => state.PhenomenonCategory.entities)),
      state$.pipe(map((state: AppState) => state.App?.systemFlags?.P40_55336_MIGRATED_CONFIGURATION_WRITES ?? '[]'))
    ),
    concatMap(([phenomenonCategory, entities, migratedWrites]) => {
      phenomenonCategory.payload = entities[phenomenonCategory.payload.id];
      return deletePhenomenonCategory(phenomenonCategory, migratedWrites).pipe(
        map(response => {
          notification('success', 'Category deleted!');
          return actions.deletePhenomenonCategorySuccess(phenomenonCategory.payload);
        }),
        catchError((error: any) => {
          const errorMsg = notifyErrorOrFallback(error, 'Error deleting category!');
          return of(actions.deletePhenomenonCategoryFailure(errorMsg));
        })
      );
    })
  );
